<template>
  <div class="mt-3">
    <v-row class="mb-3">
      <v-col cols="12">
        <p class="text-primary font-weight-medium mb-0">Assemblage(s) selectionné(s)</p>
        <div v-if="!hasAssociatedComposite">Aucun</div>
        <div v-else>
          <v-chip
            v-for="composite in associatedComposites"
            :key="composite.id"
            closable
            @update:model-value="removeFromAssociatedComposites(composite)"
          >
            {{ composite.name }}
          </v-chip>
        </div>
      </v-col>
    </v-row>
    <v-card>
      <v-alert variant="outlined" color="error" icon="mdi-alert" :value="true" v-if="showError">
        {{ nameError }}
      </v-alert>
      <v-toolbar flat density="compact" color="transparent" height="100%">
        <v-row class="fill-height align-center">
          <v-col cols="6">
            <v-text-field
              v-model="search"
              clear-icon="mdi-close"
              append-inner-icon="mdi-magnify"
              label="Recherche"
              @update:model-value="searchTrigger"
              :error-messages="getErrorMessage"
              :error="searchError"
              :persistent-hint="showHint"
              :hint="hintText"
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
      </v-toolbar>
      <v-data-table-server
        v-model="associatedComposites"
        return-object
        item-value="id"
        :headers="headers"
        :items="composites"
        :hide-default-footer="hideActions"
        class="elevation-0"
        :items-per-page-options="rowsPerPageItems"
        :loading="loadingData"
        :no-data-text="noDataText"
        :items-length="totalElements"
        :items-per-page-text="rowsPerPageText"
        page-text="Lignes {0} - {1} de {2}"
        v-model:options="options"
      >
        <template v-slot:item="props">
          <tr @click="onItemSelect(props.item)">
            <td>
              <v-checkbox
                true-icon="mdi-checkbox-marked"
                false-icon="mdi-checkbox-blank-outline"
                :model-value="props.item.selected"
                color="primary"
                hide-details
              ></v-checkbox>
            </td>
            <td>{{ props.item.name }}</td>
          </tr>
        </template>
      </v-data-table-server>
    </v-card>
  </div>
</template>

<script>
import { GetAll, GetSearch } from '@/_services/composites';
import { paginationMixin } from '@/classes/helper/paginationHelper';
export default {
  name: 'ModalCustomMaterialComposites',
  mixins: [paginationMixin],
  props: {
    material: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    forceResetComposites: {
      type: Boolean,
      required: true,
    },
    hideActions: Boolean,
  },
  data() {
    return {
      dialogOpened: false,
      totalElements: 0,
      loadingData: true,
      error: false,
      searchError: false,
      showHint: false,
      search: '',
      oldSearch: '',
      alertData: false,
      outline: true,
      showError: false,
      alertColor: 'info',
      alertIcon: 'info',
      hintText: 'Veuillez entrer au moins 3 caractères pour la recherche.',
      noDataText: 'Aucune donnée pour le moment',
      noResultsText: 'Chargement',
      rowsPerPageText: 'Éléments par page',
      nameError: 'Une erreur est survenue, veuillez vérifier vos informations et réessayer.',
      headers: [
        {
          title: '',
          align: 'start',
          key: 'column-0',
          sortable: false,
          width: '50',
        },
        {
          title: 'Nom',
          align: 'start',
          key: 'name',
          sortable: false,
        },
      ],
      composites: [],
      rowsPerPageItems: [10, 25, 50, 100, 500],
      pageKey: 'materialsCustomComponents',
      associatedComposites: this.material.associatedComposites,
    };
  },
  computed: {
    hasAssociatedComposite() {
      return this.associatedComposites.length > 0;
    },
    getErrorMessage() {
      if (this.searchError) {
        return this.noDataText;
      }
      return '';
    },
  },
  methods: {
    getDataFromApi() {
      this.showAlert('', 'info');
      this.loadingData = true;
      const { page, rowsPerPage } = this.pagination;

      GetAll(rowsPerPage, (page - 1) * rowsPerPage)
        .then(rep => {
          this.composites = rep.composites;
          this.totalElements = rep.itemCount;
          this.loadingData = false;
          this.showHint = true;
        })
        .catch(() => {
          this.error = true;
          this.composites = [];
          this.search = '';
          this.oldSearch = '';
          this.showAlert('', 'error');
        })
        .finally(() => {
          this.setSelectedComposites();
        });
    },
    getSearch() {
      this.loadingData = true;
      let searchParam = this.search.trim();

      const { page, rowsPerPage } = this.pagination;

      GetSearch(searchParam, rowsPerPage, (page - 1) * rowsPerPage)
        .then(rep => {
          this.alertData = false;
          this.composites = rep.composites;
          this.totalItems = rep.itemCount;
          this.loadingData = false;
          this.searchError = false;
          if (this.totalItems === 0) {
            this.error = true;
            this.showAlert('', 'warning');
            this.searchError = true;
          }
        })
        .catch(() => {
          if (page > 1) {
            GetSearch(searchParam, rowsPerPage, 0)
              .then(rep => {
                this.alertData = false;
                this.composites = rep.composites;
                this.totalItems = rep.itemCount;
                this.loadingData = false;
                this.searchError = false;
                if (this.totalItems === 0) {
                  this.error = true;
                  this.showAlert('', 'warning');
                  this.searchError = true;
                }
              })
              .catch(() => {
                this.error = true;
                this.composites = [];
                this.search = '';
                this.oldSearch = '';
                this.showAlert('', 'error');
              });
          } else {
            this.error = true;
            this.composites = [];
            this.search = '';
            this.oldSearch = '';
            this.showAlert('', 'error');
          }
        })
        .finally(() => {
          this.setSelectedComposites();
        });
    },
    searchTrigger() {
      if (!this.search) {
        this.search = '';
      }
      let tempSearch = this.search.trim();
      if (tempSearch.length >= 3 && tempSearch !== this.oldSearch.trim()) {
        this.oldSearch = this.search;
        this.getSearch();
        this.hintText = '';
        this.showHint = false;
      } else if (tempSearch.length < 3 && this.oldSearch.trim() !== '' && this.composites.length >= 0) {
        this.oldSearch = '';
        this.composites = [];
        this.hintText = 'Veuillez entrer au moins 3 caractères pour la recherche.';
        this.showAlert('', 'info');
        this.showHint = true;
        this.searchError = false;
      } else {
        if (tempSearch.length < 3 && this.search !== this.oldSearch) {
          this.alertData = true;
          this.showHint = true;
        }
        if (this.search.trim() !== this.oldSearch.trim()) {
          this.searchError = false;
        }
      }
      if (this.composites.length === 0 && tempSearch.length < 3 && (!this.searchError || !this.error)) {
        this.getDataFromApi();
      }
      this.error = false;
    },
    showAlert(msg, alertType) {
      this.alertData = false;
      switch (alertType) {
        case 'info':
          this.outline = true;
          this.alertColor = 'info';
          this.alertIcon = 'info';
          this.noDataText = 'Chargement.';
          break;
        case 'warning':
          this.outline = false;
          this.alertColor = 'orange';
          this.alertIcon = 'warning';
          this.noDataText = `Votre recherche pour "${this.search.trim()}" n'a retourné aucun résultat.`;
          this.error = true;
          break;
        case 'error':
          this.outline = false;
          this.alertColor = 'error';
          this.alertIcon = 'error';
          this.noDataText = 'Une erreur est survenue. Veuillez réessayer.';
          this.error = true;
          break;
        default:
          break;
      }
      if (msg.trim() !== '') {
        this.noDataText = msg;
      }
      this.alertData = true;
    },

    setSelectedComposites() {
      this.composites = this.composites.map(x => ({
        ...x,
        selected: this.associatedComposites.some(a => a.id === x.id),
      }));
    },
    onItemSelect(composite) {
      composite.selected = !composite.selected;
      if (composite.selected) {
        this.associatedComposites.push({
          id: composite.id,
          name: composite.name,
        });
      } else {
        this.associatedComposites = this.associatedComposites.filter(x => x.id !== composite.id);
      }
    },
    removeFromAssociatedComposites(composite) {
      this.associatedComposites = this.associatedComposites.filter(x => x.id !== composite.id);
      this.composites.find(x => x.id === composite.id).selected = false;
    },
    resetAssociatedComposites() {
      this.associatedComposites = [];
      this.pagination.page = 1;
      this.pagination.rowsPerPage = 10;
      this.search = '';
    },
  },

  watch: {
    show(val) {
      if (val === true) {
        this.dialogOpened = true;
        this.getDataFromApi();
      }
    },
    material: {
      handler(newVal) {
        this.associatedComposites = newVal.associatedComposites;
        this.setSelectedComposites();
      },
      deep: true,
    },
    pagination: {
      handler() {
        if (this.dialogOpened) {
          if (this.search.trim().length < 3) {
            this.getDataFromApi();
          } else {
            this.getSearch();
          }
        }
      },
      deep: true,
    },
    forceResetComposites(val) {
      if (val) {
        this.resetAssociatedComposites();
        this.$emit('update-force-reset-composites', false);
      }
    },
    associatedComposites: {
      handler(newVal) {
        this.$emit('update-associated-composites', newVal);
      },
      deep: true,
    },
  },
};
</script>
